import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import posthog from 'posthog-js';

import { CLIENT_EVENTS, track } from '../analytics/events';

export interface User {
  id: string;
  email: string;
  fullName: string;
  firstName: string;
  lastName: string;
  role: 'agent' | 'broker' | 'admin' | 'transaction_coordinator';
  phone?: string;
  photoUrl?: string;
  agentId?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  accessToken: string | null;
  login: (user: User, accessToken: string | null) => void;
  logout: () => void;
}

type BrowserStorageLike = {
  getItem: (name: string) => string | null;
  setItem: (name: string, value: string) => void;
  removeItem: (name: string) => void;
};

const memoryStorage = new Map<string, string>();

const resolveBrowserStorage = (): BrowserStorageLike | null => {
  const candidate =
    typeof window !== 'undefined'
      ? window.localStorage
      : ((globalThis as { localStorage?: unknown }).localStorage ?? null);

  if (
    candidate &&
    typeof (candidate as BrowserStorageLike).getItem === 'function' &&
    typeof (candidate as BrowserStorageLike).setItem === 'function' &&
    typeof (candidate as BrowserStorageLike).removeItem === 'function'
  ) {
    return candidate as BrowserStorageLike;
  }

  return null;
};

const safeStateStorage: StateStorage = {
  getItem: (name) => {
    const browserStorage = resolveBrowserStorage();
    return browserStorage ? browserStorage.getItem(name) : (memoryStorage.get(name) ?? null);
  },
  setItem: (name, value) => {
    const browserStorage = resolveBrowserStorage();
    if (browserStorage) {
      browserStorage.setItem(name, value);
      return;
    }
    memoryStorage.set(name, value);
  },
  removeItem: (name) => {
    const browserStorage = resolveBrowserStorage();
    if (browserStorage) {
      browserStorage.removeItem(name);
      return;
    }
    memoryStorage.delete(name);
  },
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      accessToken: null,
      login: (user: User, accessToken: string | null) => {
        /* v8 ignore start */
        posthog.identify(user.id, {
          email: user.email,
          name: user.fullName,
          role: user.role,
        });
        track(CLIENT_EVENTS.LOGIN_SUCCEEDED, { role: user.role });
        /* v8 ignore stop */
        set({ user, isAuthenticated: true, accessToken });
      },
      logout: () => {
        /* v8 ignore start */
        track(CLIENT_EVENTS.LOGOUT);
        posthog.reset();
        /* v8 ignore stop */
        set({ user: null, isAuthenticated: false, accessToken: null });
      },
    }),
    {
      name: 'cda-auth-storage',
      storage: createJSONStorage(() => safeStateStorage),
    }
  )
);
